import { defineStore } from "pinia";
import { api } from "@/app/services/api.service";

export interface BusinessRegistryState {
  company_registry: any
  business_name_registry: any
  building_society_registry: any
  black_list_registry: any
  mda_list_registry: any
  entity_file: any
  edms_file: any
  error: any
  edms_error: any
}

export interface RegistryFilters {
  page: number
  limit: number
  offset: number
  search?: string
  status?: string
}

export const useBusinessRegistryStore = defineStore('businessRegistry', {
  state: (): BusinessRegistryState => ({
    company_registry: null,
    business_name_registry: null,
    building_society_registry: null,
    black_list_registry: null,
    mda_list_registry: null,
    entity_file: null,
    edms_file: null,
    error: null,
    edms_error: null,
  }),

  getters: {
    getEntityFile: (state) => state.entity_file,
    getEntityFileError: (state) => state.edms_error,
    getEDMSFile: (state) => state.edms_file,
    getCompanyRegistry: (state) => state.company_registry,
    getBuildingSocietyRegistry: (state) => state.building_society_registry,
    getBlacklistRegistry: (state) => state.black_list_registry,
    getMDAListRegistry: (state) => state.mda_list_registry,
    getBusinessNameRegistry: (state) => state.business_name_registry,
    getRegistryError: (state) => state.error,
  },

  actions: {
    async fetchCompanyRegistry(data: RegistryFilters) {
      try {
        const response = await api().get('/central-registry/api/companies?page=' + data.page + '&limit=' + data.limit + '&offset=' + data.offset)
        this.company_registry = response.data.content
        this.error = null
      } catch (error: any) {
        this.error = error.response?.data
      }
    },

    async searchCompanyRegistry(data: any) {
      try {
        const response = await api().post('/central-registry/api/companies/search', data)
        this.company_registry = response.data
        this.error = null
      } catch (error: any) {
        this.error = error.response?.data
      }
    },

    async fetchCompanyByBRN(brn: string) {
      try {
        const response = await api().get('/central-registry/api/companies/' + brn + '/info')
        this.entity_file = response.data
      } catch (error: any) {
        this.error = error.response?.data
      }
    },

    async fetchBuildingSocietyRegistry(data: RegistryFilters) {
      try {
        const response = await api().get('/central-registry/api/building-societies?page=' + data.page + '&limit=' + data.limit + '&offset=' + data.offset)
        this.building_society_registry = response.data.content
        this.error = null
      } catch (error: any) {
        this.error = error.response?.data
      }
    },

    async fetchBuildingSocietyByBRN(brn: string) {
      try {
        const response = await api().get('/central-registry/api/building-societies/' + brn + '/by-brn')
        this.entity_file = response.data
      } catch (error: any) {
        this.error = error.response?.data
      }
    },

    async fetchBusinessNameRegistry(data: RegistryFilters) {
      try {
        const response = await api().get('/central-registry/api/business-names?page=' + data.page + '&limit=' + data.limit + '&offset=' + data.offset)
        this.business_name_registry = response.data.content
        this.error = null
      } catch (error: any) {
        this.error = error.response?.data
      }
    },

    async searchBusinessNameRegistry(data: any) {
      try {
        const response = await api().post('/central-registry/api/business-names/search', data)
        this.business_name_registry = response.data
        this.error = null
      } catch (error: any) {
        this.error = error.response?.data
      }
    },

    async fetchBusinessNameByBRN(brn: string) {
      try {
        const response = await api().get('/central-registry/api/business-names/' + brn + '/by-brn')
        this.entity_file = response.data
      } catch (error: any) {
        this.error = error.response?.data
      }
    },

    async fetchCompanyEDMSFile(data: any) {
      try {
        const response = await api().post('/integration/api/edms/company/files/all', data)
        this.edms_file = response.data
      } catch (error: any) {
        this.edms_error = error.response?.data
      }
    },

    async fetchBusinessNameEDMSFile(data: any) {
      try {
        const response = await api().post('/integration/api/edms/business-name/files/all', data)
        this.edms_file = response.data
      } catch (error: any) {
        this.error = error.response?.data
      }
    },

    async fetchSocietyEDMSFile(data: any) {
      try {
        const response = await api().post('/integration/api/edms/company/files/all', data)
        this.edms_file = response.data
      } catch (error: any) {
        this.error = error.response?.data
      }
    },

    async uploadBatchBrnsForStrikingOff(data: any) {
      try {
        const response = await api().post('/data-update/blacklisted-brns', data, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })
        this.entity_file = response.data
      } catch (error: any) {
        this.error = error.response?.data
      }
    },

    async uploadBatchBrnsForNameChangeStrikingOff(data: any) {
      try {
        const response = await api().post("/incorporation/restorations/manual/strike-off", data, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        this.entity_file = response.data
      } catch (error: any) {
        this.error = error.response?.data
      }
    },

    async fetchBlackListRegistry(data: RegistryFilters) {
      try {
        const response = await api().get('/data-update/blacklisted-brns?page=' + data.page + '&pageSize=' + data.limit + '&offset=' + data.offset + '&status=' + data.status + '&search=' + data.search)
        this.black_list_registry = response.data
        this.error = null
      } catch (error: any) {
        this.error = error.response?.data
      }
    },

    async fetchMDAListRegistry(data: RegistryFilters) {
      try {
        const response = await api().get('/configuration/mda-registry?page=' + data.page + '&limit=' + data.limit + '&search=' + data.search)
        this.mda_list_registry = response.data
        this.error = null;
      } catch (error: any) {
        this.error = error.response?.data;
      }
    },

    async uploadBatchBrnsForDeregistration(data: any) {
      try {
        const response = await api().post("/data-update/de-registrations", data);
        this.entity_file = response.data;
        this.error = null
      } catch (error: any) {
        this.error = error.response?.data
      }
    },
  },
})
